/**
 * CustomerDrawer — premium slide-over for a customer.
 * Opened from clicking a customer name in the Invoices table.
 * Reuses the drawer pattern from EmployeeDrawer (framer-motion AnimatePresence,
 * mobile = bottom-sheet with swipe-down). Renders contact info, outstanding
 * balance, recent invoices, and payment history.
 */
import { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, Mail, Phone, MapPin, DollarSign, FileText, Receipt, Eye, Plus,
} from 'lucide-react';

function fmtMoney(n) {
  return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function initials(name = '') {
  return name.split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();
}

function avatarColor(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `hsl(${h}, 55%, 52%)`;
}

export default function CustomerDrawer({
  open, onClose, customer, invoices, isMobile,
  onCreateInvoice, onOpenInvoice,
}) {
  const stats = useMemo(() => {
    if (!customer) return null;
    const owned = (invoices || []).filter((inv) => inv.customerId === customer.id);
    let outstanding = 0;
    let lifetime = 0;
    const payments = [];
    owned.forEach((inv) => {
      const total = Number(inv.total || 0);
      const paidArr = inv.payments || [];
      const paidSum = paidArr.reduce((s, p) => s + Number(p.amount || 0), 0);
      lifetime += paidSum;
      if (inv.status !== 'paid') outstanding += Math.max(0, total - paidSum);
      paidArr.forEach((p) => payments.push({ ...p, invoiceNumber: inv.invoiceNumber }));
    });
    return {
      outstanding,
      lifetime,
      invoiceCount: owned.length,
      recentInvoices: owned
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 6),
      recentPayments: payments
        .sort((a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0))
        .slice(0, 6),
    };
  }, [customer, invoices]);

  if (!customer) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="customer-drawer-backdrop"
            style={{
              position: 'fixed', inset: 0, zIndex: 10500,
              background: 'rgba(15,23,42,0.45)',
              backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            }}
          />
          <motion.aside
            data-testid="customer-drawer"
            role="dialog" aria-modal="true"
            initial={isMobile ? { y: '100%' } : { x: '100%' }}
            animate={isMobile ? { y: 0 } : { x: 0 }}
            exit={isMobile ? { y: '100%' } : { x: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            drag={isMobile ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (isMobile && info.offset.y > 120) onClose();
            }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: isMobile ? '100%' : 'min(480px, 96vw)',
              maxWidth: '100vw', zIndex: 10501,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              borderTopLeftRadius: isMobile ? 16 : 0,
              borderTopRightRadius: isMobile ? 16 : 0,
              boxShadow: '-20px 0 60px -10px rgba(15,23,42,0.25)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
                <div style={{ width: 40, height: 4, background: '#cbd5e1', borderRadius: 2 }} />
              </div>
            )}
            <div
              style={{
                position: 'relative', padding: '24px 24px 20px',
                background: `linear-gradient(135deg, ${avatarColor(customer.id)}20 0%, #ffffff 100%)`,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={onClose} data-testid="customer-drawer-close" aria-label="Close"
                style={{
                  position: 'absolute', top: 16, right: 16,
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(255,255,255,0.85)', border: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    width: 60, height: 60, borderRadius: '50%',
                    background: avatarColor(customer.id), color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.10)', border: '3px solid #fff',
                  }}
                >
                  {initials(customer.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    {customer.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {stats?.invoiceCount || 0} {stats?.invoiceCount === 1 ? 'invoice' : 'invoices'} on file
                  </div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 24px' }}>
              {/* Outstanding + Lifetime */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div
                  data-testid="customer-outstanding"
                  style={{
                    padding: '14px 16px', borderRadius: 12,
                    background: stats?.outstanding > 0 ? 'rgba(220,38,38,0.06)' : '#f8fafc',
                    border: `1px solid ${stats?.outstanding > 0 ? 'rgba(220,38,38,0.20)' : '#e5e7eb'}`,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Outstanding
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: stats?.outstanding > 0 ? '#b91c1c' : '#0f172a' }}>
                    {fmtMoney(stats?.outstanding)}
                  </div>
                </div>
                <div
                  data-testid="customer-lifetime"
                  style={{ padding: '14px 16px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e5e7eb' }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Lifetime Paid
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: '#0f172a' }}>
                    {fmtMoney(stats?.lifetime)}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <SectionTitle>Contact</SectionTitle>
              <Row icon={<Mail size={13} />} value={customer.email || '—'} />
              <Row icon={<Phone size={13} />} value={customer.phone || '—'} />
              <Row icon={<MapPin size={13} />} value={customer.address || '—'} />

              {/* Recent invoices */}
              <SectionTitle>Recent Invoices</SectionTitle>
              {(stats?.recentInvoices || []).length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>No invoices yet</div>
              ) : (
                stats.recentInvoices.map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => onOpenInvoice?.(inv)}
                    data-testid={`customer-drawer-invoice-${inv.id}`}
                    style={{
                      all: 'unset', cursor: 'pointer', width: '100%',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 10,
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileText size={14} color="#64748b" />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                          #{inv.invoiceNumber}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {new Date(inv.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {fmtMoney(inv.total)}
                      </div>
                      <StatusPill status={inv.status} />
                      <Eye size={13} color="#94a3b8" />
                    </div>
                  </button>
                ))
              )}

              {/* Payments */}
              {(stats?.recentPayments || []).length > 0 && (
                <>
                  <SectionTitle>Payment History</SectionTitle>
                  {stats.recentPayments.map((p, i) => (
                    <div
                      key={`${p.invoiceNumber}-${i}`}
                      style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '8px 12px', fontSize: 12, color: '#374151',
                        borderBottom: '1px dashed #e5e7eb',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Receipt size={12} />
                        #{p.invoiceNumber} · {p.method || 'cash'}
                      </span>
                      <span>
                        <span style={{ color: '#94a3b8', marginRight: 8 }}>
                          {p.date ? new Date(p.date).toLocaleDateString() : ''}
                        </span>
                        <span style={{ fontWeight: 600, color: '#16a34a' }}>
                          {fmtMoney(p.amount)}
                        </span>
                      </span>
                    </div>
                  ))}
                </>
              )}

              {/* Action */}
              <button
                data-testid="customer-drawer-create-invoice"
                onClick={() => onCreateInvoice?.(customer)}
                style={{
                  marginTop: 20, width: '100%', padding: '12px',
                  borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  boxShadow: '0 4px 12px rgba(0,115,234,0.30)',
                }}
              >
                <Plus size={14} /> New Invoice for {customer.name.split(' ')[0]}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 22, marginBottom: 8 }}>
      {children}
    </div>
  );
}
function Row({ icon, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: '#1f2937', borderBottom: '1px dashed #e5e7eb' }}>
      <span style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      <span>{value}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: { c: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
    pending: { c: '#0073ea', bg: 'rgba(0,115,234,0.10)' },
    partial: { c: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    overdue: { c: '#dc2626', bg: 'rgba(220,38,38,0.10)' },
    draft: { c: '#64748b', bg: 'rgba(100,116,139,0.10)' },
  };
  const s = map[status] || map.draft;
  return (
    <span
      style={{
        padding: '2px 8px', borderRadius: 999,
        background: s.bg, color: s.c, fontSize: 10, fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status || 'draft'}
    </span>
  );
}

// IconDollar export reserved for future use
export { DollarSign };
